import React, { FC, ReactElement, useCallback, useMemo } from 'react';
import { NotificationEntity, UserEntity } from 'app/interfaces/entity';
import { BaseNotificationTile } from 'modules/notifications/components/BaseNotificationTile';
import { NotificationType } from '@okletsplay/core';
import { UserAvatar } from 'shared/components/UserAvatar';
import { NotificationGameIcon } from 'modules/notifications/components/NotificationGameIcon';
import { Box } from '@mui/material';

interface MatchNotificationTileProps {
  notification: NotificationEntity;
  onClick?: (notification: NotificationEntity) => void;
}

export const MatchNotificationTile: FC<MatchNotificationTileProps> = ({
  notification,
  onClick = () => null,
}) => {
  const initiator = useMemo<Partial<UserEntity>>(() => notification.initiator, [notification]);
  const matchGameIcon = useMemo(() => notification.match?.game?.icon ?? '', [notification]);
  const matchTitle = useMemo(() => notification.match?.game?.title ?? '', [notification]);

  const notificationTemplate: Partial<Record<NotificationType, ReactElement | null>> =
    useMemo(() => {
      return {
        [NotificationType.MatchInvitedToPlay]: (
          <BaseNotificationTile
            image={<UserAvatar user={initiator} />}
            message={`${notification.initiator.userName} invited you to play ${matchTitle}!`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.MatchInvitationAccepted]: (
          <BaseNotificationTile
            image={<UserAvatar user={initiator} />}
            message={`${notification.initiator.userName} accepted your invite to play ${matchTitle}!`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.MatchInvitationDeclined]: (
          <BaseNotificationTile
            image={<UserAvatar user={initiator} />}
            message={`${notification.initiator.userName} declined your invite to play ${matchTitle}!`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.MatchJoinRequest]: (
          <BaseNotificationTile
            image={<UserAvatar user={initiator} />}
            message={`${notification.initiator.userName} wants to join your ${matchTitle} match!`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.MatchJoined]: (
          <BaseNotificationTile
            image={<UserAvatar user={initiator} />}
            message={`${notification.initiator.userName} joined your ${matchTitle} match!`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.MatchJoinRequestAccepted]: (
          <BaseNotificationTile
            image={<UserAvatar user={initiator} />}
            message={`${notification.initiator.userName} accepted your join request to play ${matchTitle}!`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.MatchJoinRequestDeclined]: (
          <BaseNotificationTile
            image={<UserAvatar user={initiator} />}
            message={`${notification.initiator.userName} declined your join request to play ${matchTitle}`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.MatchDeclined]: (
          <BaseNotificationTile
            image={<UserAvatar user={initiator} />}
            message={`${notification.initiator.userName} declined your ${matchTitle} match.`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.MatchFinishedAborted]: (
          <BaseNotificationTile
            image={<NotificationGameIcon icon={matchGameIcon} />}
            message={`Your ${matchTitle} match has been aborted.`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.MatchFinishedInTie]: (
          <BaseNotificationTile
            image={<NotificationGameIcon icon={matchGameIcon} />}
            message={notification?.message ?? `Your ${matchTitle} match has ended in a tie.`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.MatchFinishedWon]: (
          <BaseNotificationTile
            image={<NotificationGameIcon icon={matchGameIcon} />}
            message={notification?.message ?? `You won in your ${matchTitle} match!`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.MatchFinished]: (
          <BaseNotificationTile
            image={<NotificationGameIcon icon={matchGameIcon} />}
            message={notification?.message ?? `${matchTitle} match has finished.`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
      };
    }, [initiator, matchGameIcon, matchTitle, notification]);

  const onClickHandler = useCallback(() => {
    onClick?.(notification);
  }, [notification, onClick]);

  return <Box onClick={onClickHandler}>{notificationTemplate[notification.type]}</Box>;
};
